/*#
# b3d-star

Procedural star: a sphere with a shader surface. What makes it read as a ball
rather than a white disc is **limb darkening**: toward the edge you look
through cooler, higher layers, so it dims and reddens. Under that, a seething
granulation and a few sunspots. Spectral class sets the colour temperature.
It is unlit, because it is the light.

An optional corona (a glow shell) surrounds it, and `activity` adds a few
**active regions**, turning with the star: over the edge each is a prominence
(a glowing loop arching out of the limb, drawn in the corona), and now and then
one flares, its loop swelling while a bright patch flashes on the disc. The
same material draws the star in a [star system](/b3d-star-system/).

## Demo

```js
import { b3d, b3dSun, b3dSkybox, b3dLight, b3dStar, b3dSphere, label3d, slider3d, toggle3d, select3d } from 'tosijs-3d'
import { tosi } from 'tosijs'

const { demo } = tosi({
  demo: {
    spectralClass: 'G',
    radius: 20,
    surfaceDetail: 0.5,
    coronaSize: 0.3,
    glowIntensity: 1.1,
    rotationSpeed: 0.02,
    wireframe: false,
    pointLight: true,
    lightIntensity: 1.0,
  },
})

const star = b3dStar({
  seed: 42,
  radius: demo.radius,
  subdivisions: 32,
  spectralClass: demo.spectralClass,
  surfaceDetail: demo.surfaceDetail,
  coronaSize: demo.coronaSize,
  glowIntensity: demo.glowIntensity,
  rotationSpeed: demo.rotationSpeed,
  wireframe: demo.wireframe,
  pointLight: demo.pointLight,
  lightIntensity: demo.lightIntensity,
})

const scene = b3d(
  {
    frameRate: 60,
    clearColor: '#000000',
    panelTitle: 'Star',
    scenePanel: () => [
      select3d({ label: 'spectral class', value: demo.spectralClass, options: ['O', 'B', 'A', 'F', 'G', 'K', 'M'] }),
      slider3d({ label: 'radius', value: demo.radius, min: 5, max: 50, step: 1 }),
      slider3d({ label: 'surface detail', value: demo.surfaceDetail, min: 0, max: 1, step: 0.05 }),
      slider3d({ label: 'corona size', value: demo.coronaSize, min: 0, max: 0.5, step: 0.01 }),
      slider3d({ label: 'glow', value: demo.glowIntensity, min: 0, max: 3, step: 0.1 }),
      slider3d({ label: 'rotation', value: demo.rotationSpeed, min: 0, max: 0.2, step: 0.005 }),
      slider3d({ label: 'light intensity', value: demo.lightIntensity, min: 0, max: 5, step: 0.1 }),
      toggle3d({ label: 'wireframe', value: demo.wireframe }),
      toggle3d({ label: 'point light', value: demo.pointLight }),
    ],
    sceneCreated(el, BABYLON) {
      const camera = new BABYLON.ArcRotateCamera(
        'orbit-cam',
        -Math.PI / 2,
        Math.PI / 3,
        80,
        BABYLON.Vector3.Zero(),
        el.scene
      )
      camera.lowerRadiusLimit = 30
      camera.upperRadiusLimit = 300
      camera.minZ = 0.5
      camera.maxZ = 2000
      camera.attachControl(el.querySelector('canvas'), true)
      el.setActiveCamera(camera)
    },
  },
  b3dLight({ intensity: 0.1 }),
  star,
  b3dSphere({ radius: 5, x: 40, y: 0, z: 0, color: '#888888' }),
  b3dSphere({ radius: 5, x: -40, y: 0, z: 0, color: '#888888' }),
)

preview.append(
  scene,
)

for (const key of ['radius', 'surfaceDetail']) {
  demo[key].observe(() => {
    star.regenerate()
  })
}
for (const key of ['spectralClass', 'coronaSize', 'glowIntensity', 'wireframe', 'rotationSpeed', 'pointLight', 'lightIntensity']) {
  demo[key].observe(() => {
    star.updateOptions()
  })
}
```
```css
.preview { display: flex; flex-direction: column; height: 100%; }
tosi-b3d { flex: 1; min-height: 0; width: 100%; }
```

Scroll to zoom, drag to orbit. The settings are in the scene panel (the owl).

## Attributes

| Attribute | Default | Description |
| --- | --- | --- |
| `seed` | `12345` | Noise seed for surface features |
| `radius` | `20` | Star radius |
| `subdivisions` | `32` | Grid subdivisions per cube face |
| `spectralClass` | `'G'` | Spectral class: O, B, A, F, G, K, M |
| `surfaceDetail` | `0.5` | Surface noise intensity (0=uniform, 1=strong spots) |
| `coronaSize` | `0.15` | Corona thickness as fraction of radius (0=none). Prominences live in it, so they are as tall as it allows |
| `activity` | `0.6` | Prominences over the edge and flares on the disc, 0 to 1 (0 none) |
| `rotationSpeed` | `0.02` | Auto-rotation (radians/sec) |
| `wireframe` | `false` | Debug wireframe |
| `glowIntensity` | `2.0` | Emissive brightness multiplier |
| `pointLight` | `false` | Create a point light at star center |
| `lightIntensity` | `1.0` | Point light intensity |
| `lightRange` | `500` | Point light range |

*/
/*{ "parent": "space", "order": 30 }*/

import { B3dChild, sceneDelta } from './b3d-utils.js'
import * as BABYLON from '@babylonjs/core'
import { createCoronaMaterial, createStarMaterial } from './star-material.js'
import type { B3d } from './tosi-b3d.js'
import { PerlinNoise } from './perlin-noise.js'

const CUBE_FACES: { origin: number[]; right: number[]; up: number[] }[] = [
  { origin: [1, -1, 1], right: [0, 0, -2], up: [0, 2, 0] },
  { origin: [-1, -1, -1], right: [0, 0, 2], up: [0, 2, 0] },
  { origin: [-1, 1, 1], right: [2, 0, 0], up: [0, 0, -2] },
  { origin: [-1, -1, -1], right: [2, 0, 0], up: [0, 0, 2] },
  { origin: [-1, -1, 1], right: [2, 0, 0], up: [0, 2, 0] },
  { origin: [1, -1, -1], right: [-2, 0, 0], up: [0, 2, 0] },
]

// Spectral class → base color (RGB)
/*
STYLISED, not a blackbody: each class reads as its own colour. A real
blackbody runs blue-white → white → yellow → orange → red, and a star never
looks violet (violet is mixed in the eye, there is no violet light to see),
so O and B carry only a trace of it, as a hint that they are the hottest.
*/
const SPECTRAL_COLORS: Record<string, [number, number, number]> = {
  O: [0.95, 0.92, 1.0],
  B: [0.72, 0.74, 1.0],
  A: [0.55, 0.72, 1.0],
  F: [0.76, 1.0, 0.7],
  G: [1.0, 0.94, 0.55],
  K: [1.0, 0.75, 0.45],
  M: [1.0, 0.5, 0.25],
}

function getSpectralColor(cls: string): [number, number, number] {
  return SPECTRAL_COLORS[cls.toUpperCase()] || SPECTRAL_COLORS.G
}

export class B3dStar extends B3dChild {
  static preferredTagName = 'tosi-b3d-star'

  static shadowStyleSpec = {
    ':host': {
      display: 'none',
    },
  }

  static initAttributes = {
    seed: 12345,
    radius: 20,
    subdivisions: 32,
    spectralClass: 'G',
    surfaceDetail: 0.5,
    coronaSize: 0.3,
    rotationSpeed: 0.02,
    wireframe: false,
    glowIntensity: 1.1,
    pointLight: false,
    lightIntensity: 1.0,
    lightRange: 500,
    // Prominences over the edge and flares on the disc, 0…1 (0 none).
    activity: 0.6,
  }

  declare seed: number
  declare radius: number
  declare subdivisions: number
  declare spectralClass: string
  declare surfaceDetail: number
  declare coronaSize: number
  declare activity: number
  declare rotationSpeed: number
  declare wireframe: boolean
  declare glowIntensity: number
  declare pointLight: boolean
  declare lightIntensity: number
  declare lightRange: number

  owner: B3d | null = null

  private noise!: PerlinNoise
  private starMesh: BABYLON.Mesh | null = null
  private coronaMesh: BABYLON.Mesh | null = null
  private starLight: BABYLON.PointLight | null = null
  private rootNode: BABYLON.TransformNode | null = null
  private registered = false
  private _beforeRender: (() => void) | null = null

  content = () => ''

  sceneReady(owner: B3d, scene: BABYLON.Scene) {
    this.owner = owner

    const attrs = this as any
    this.noise = new PerlinNoise(attrs.seed)

    this.rootNode = new BABYLON.TransformNode('star-root', scene)
    this.buildStar()
    this.buildCorona()
    this.buildLight()

    this._beforeRender = () => this.update()
    scene.registerBeforeRender(this._beforeRender)
  }

  sceneDispose() {
    if (this.owner && this._beforeRender) {
      this.owner.scene.unregisterBeforeRender(this._beforeRender)
    }
    this.starLight?.dispose()
    this.starLight = null
    this.rootNode?.dispose()
    this.rootNode = null
    this.starMesh = null
    this.coronaMesh = null
    this.owner = null
  }

  private update() {
    if (this.rootNode == null) return
    const attrs = this as any
    const speed: number = attrs.rotationSpeed
    if (speed > 0) {
      const dt = sceneDelta(this.owner!.scene)
      this.rootNode.rotation.y += speed * dt
    }
  }

  private buildStar() {
    if (this.owner == null || this.rootNode == null) return
    const attrs = this as any
    const radius: number = attrs.radius
    const subs: number = attrs.subdivisions
    const detail: number = attrs.surfaceDetail
    const [cr, cg, cb] = getSpectralColor(attrs.spectralClass)
    const glow: number = attrs.glowIntensity

    const vertsPerFace = (subs + 1) * (subs + 1)
    const totalVerts = vertsPerFace * 6
    const positions = new Float32Array(totalVerts * 3)
    const normals = new Float32Array(totalVerts * 3)
    const colors = new Float32Array(totalVerts * 4)
    const indices: number[] = []

    let vertOffset = 0
    for (let face = 0; face < 6; face++) {
      const f = CUBE_FACES[face]
      const baseVert = vertOffset

      for (let iy = 0; iy <= subs; iy++) {
        for (let ix = 0; ix <= subs; ix++) {
          const u = ix / subs
          const v = iy / subs

          const cx = f.origin[0] + f.right[0] * u + f.up[0] * v
          const cy = f.origin[1] + f.right[1] * u + f.up[1] * v
          const cz = f.origin[2] + f.right[2] * u + f.up[2] * v

          const len = Math.sqrt(cx * cx + cy * cy + cz * cz)
          const nx = cx / len
          const ny = cy / len
          const nz = cz / len

          const vi = vertOffset * 3
          positions[vi] = nx * radius
          positions[vi + 1] = ny * radius
          positions[vi + 2] = nz * radius
          normals[vi] = nx
          normals[vi + 1] = ny
          normals[vi + 2] = nz

          // Surface noise for sunspot/granulation effect
          // Use fractal noise on the sphere surface
          const noiseScale = 3.0
          const noiseVal = this.noise.fractal(
            nx * noiseScale,
            ny * noiseScale,
            nz * noiseScale,
            4
          )
          // noiseVal is roughly -1..1, map to brightness variation
          // detail=0 means uniform, detail=1 means strong variation
          const brightness = 1.0 - detail * 0.4 * (noiseVal * 0.5 + 0.5)

          const ci = vertOffset * 4
          colors[ci] = cr * brightness * glow
          colors[ci + 1] = cg * brightness * glow
          colors[ci + 2] = cb * brightness * glow
          colors[ci + 3] = 1.0

          vertOffset++
        }
      }

      for (let iy = 0; iy < subs; iy++) {
        for (let ix = 0; ix < subs; ix++) {
          const a = baseVert + iy * (subs + 1) + ix
          const b = a + 1
          const c = a + (subs + 1)
          const d = c + 1
          indices.push(a, c, b)
          indices.push(b, c, d)
        }
      }
    }

    const mesh = new BABYLON.Mesh('star', this.owner.scene)
    const vertexData = new BABYLON.VertexData()
    vertexData.positions = positions
    vertexData.indices = indices
    vertexData.normals = normals
    vertexData.colors = colors
    vertexData.applyToMesh(mesh, true)

    mesh.material = this.surfaceMaterial()
    mesh.parent = this.rootNode

    this.starMesh = mesh

    if (!this.registered) {
      this.registered = true
      this.owner.register({ meshes: [mesh] })
    }
  }

  private buildCorona() {
    if (this.owner == null || this.rootNode == null) return
    const attrs = this as any
    const coronaFrac: number = attrs.coronaSize
    if (coronaFrac <= 0) return

    const radius: number = attrs.radius
    const coronaRadius = radius * (1 + coronaFrac)
    const [cr, cg, cb] = getSpectralColor(attrs.spectralClass)
    const glow: number = attrs.glowIntensity

    const mesh = BABYLON.MeshBuilder.CreateSphere(
      'corona',
      { diameter: coronaRadius * 2, segments: 32 },
      this.owner.scene
    )

    // The glow and the prominences in it (star-material, shared with the
    // star system).
    const mat = createCoronaMaterial(this.owner.scene, {
      rgb: [cr * glow * 0.6, cg * glow * 0.6, cb * glow * 0.6],
      starRadius: radius,
      shellRadius: coronaRadius,
      seed: 1 + (Math.abs(attrs.seed) % 97),
      activity: attrs.activity,
    })

    mesh.material = mat
    mesh.parent = this.rootNode
    // Ensure corona never occludes the star
    mesh.hasVertexAlpha = true

    this.coronaMesh = mesh
  }

  private buildLight() {
    if (this.owner == null || this.rootNode == null) return
    const attrs = this as any
    if (!attrs.pointLight) return

    const [cr, cg, cb] = getSpectralColor(attrs.spectralClass)
    const light = new BABYLON.PointLight(
      'star-light',
      BABYLON.Vector3.Zero(),
      this.owner.scene
    )
    light.parent = this.rootNode
    light.intensity = attrs.lightIntensity
    light.range = attrs.lightRange
    light.diffuse = new BABYLON.Color3(cr, cg, cb)
    light.specular = new BABYLON.Color3(cr * 0.5, cg * 0.5, cb * 0.5)

    // Exclude star and corona from point light
    if (this.starMesh) light.excludedMeshes.push(this.starMesh)
    if (this.coronaMesh) light.excludedMeshes.push(this.coronaMesh)

    this.starLight = light
    this.owner.register({ lights: [light] })
  }

  /** Rebuild star mesh with current settings */
  regenerate() {
    if (this.starMesh) {
      this.starMesh.dispose()
      this.starMesh = null
    }
    if (this.coronaMesh) {
      this.coronaMesh.dispose()
      this.coronaMesh = null
    }
    if (this.starLight) {
      this.starLight.dispose()
      this.starLight = null
    }
    this.registered = false
    this.buildStar()
    this.buildCorona()
    this.buildLight()
  }

  /*
  THE SURFACE (star-material): limb darkening, granulation and spots in a
  shader. It was vertex colours under a flat emissive colour, which read as
  a white disc.
  */
  private surfaceMaterial(): BABYLON.ShaderMaterial {
    const attrs = this as any
    const mat = createStarMaterial(this.owner!.scene, {
      rgb: getSpectralColor(attrs.spectralClass) as [number, number, number],
      seed: 1 + (Math.abs(attrs.seed) % 97),
      // The old default glow (1.1) is the material's 1.
      glow: attrs.glowIntensity / 1.1,
      detail: attrs.surfaceDetail,
      activity: attrs.activity,
    })
    mat.wireframe = attrs.wireframe
    return mat
  }

  /** Update colors, corona, wireframe without full rebuild */
  updateOptions() {
    // The surface material carries the colour, glow and detail: remade.
    if (this.starMesh) {
      this.starMesh.material?.dispose()
      this.starMesh.material = this.surfaceMaterial()
    }

    // Rebuild corona (size/color may have changed)
    if (this.coronaMesh) {
      this.coronaMesh.dispose()
      this.coronaMesh = null
    }
    this.buildCorona()

    // Update or rebuild light
    if (this.starLight) {
      this.starLight.dispose()
      this.starLight = null
    }
    this.buildLight()
  }
}

export const b3dStar = B3dStar.elementCreator()
